import { ArrowLeft, Calendar, Edit, Users } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import { useJob, useUpdateJob } from '../../hooks/useJobBoard';

export default function AdminJobDetailsPage() {
  const { id = '' } = useParams();
  const query = useJob(id);
  const update = useUpdateJob();
  if (query.isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;
  if (query.isError || !query.data) return <div className="p-8 text-center text-slate-500">Job not found.</div>;
  const job = query.data;

  const setStatus = (status: 'Published' | 'Closed') => {
    if (!window.confirm(`Mark this job as ${status.toLowerCase()}?`)) return;
    update.mutate({
      id,
      data: {
        title: job.title,
        description: job.description,
        requirements: job.requirements,
        industryIds: job.industryIds ?? [],
        countryIds: job.countryIds ?? [],
        expirationDate: job.expirationDate ?? null,
        status,
      }
    });
  };
  return (
    <div className="mx-auto max-w-6xl space-y-6 p-4 text-left md:p-8">
      <Link
        to="/admin/job-board/jobs"
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500"
      >
        <ArrowLeft size={16} />
        Back to Jobs
      </Link>

      <header className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
        <div>
          <p className="text-sm font-semibold text-strivre-burgundy">
            Job Details
          </p>

          <h1 className="mt-1 text-3xl font-bold text-strivre-navy">
            {job.title}
          </h1>

          <p className="mt-2 text-sm text-slate-500">
            {job.industryNames?.join(", ") ||
              "Industry"}
            {" • "}
            {job.countryNames?.join(", ") ||
              "Country"}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {job.status !== "Published" && (
            <button
              disabled={update.isPending}
              onClick={() => setStatus("Published")}
              className="btn-primary btn-md rounded-lg bg-strivre-burgundy text-white hover:opacity-90"
            >
              Publish
            </button>
          )}

          {job.status !== "Closed" && (
            <button
              disabled={update.isPending}
              onClick={() => setStatus("Closed")}
              className="btn-md rounded-lg border border-red-200 bg-red-50 px-4 py-2 font-medium text-red-700 transition-colors hover:bg-red-100"
            >
              Close Job
            </button>
          )}

          <Link
            to={`/admin/job-board/jobs/${job.jobId}/edit`}
            className="btn-md inline-flex items-center gap-2 rounded-lg bg-strivre-navy px-4 py-2 font-medium text-white transition-colors hover:bg-opacity-90"
          >
            <Edit size={16} />
            Edit
          </Link>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <article className="space-y-6 rounded-lg border border-strivre-lightnavy bg-white p-6">
          <section>
            <h2 className="text-lg font-bold text-slate-900">
              Description
            </h2>

            <p className="mt-3 whitespace-pre-wrap leading-7 text-slate-600">
              {job.description}
            </p>
          </section>

          <section className="border-t border-slate-100 pt-6">
            <h2 className="text-lg font-bold text-slate-900">
              Requirements
            </h2>

            <p className="mt-3 whitespace-pre-wrap leading-7 text-slate-600">
              {job.requirements}
            </p>
          </section>
        </article>

        <aside className="rounded-lg border border-strivre-lightnavy bg-white p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Position Status
          </p>

          <p className="mt-2 text-lg font-bold text-slate-900">
            {job.status ?? "Draft"}
          </p>

          <div className="mt-5 space-y-3 text-sm text-slate-600">
            <p className="flex items-center gap-2">
              <Calendar size={16} />
              Expires{" "}
              {job.expirationDate
                ? new Date(job.expirationDate).toLocaleDateString()
                : "Not Set"}
            </p>
          </div>

          <Link
            to={`/admin/job-board/jobs/${job.jobId}/applicants`}
            className="btn-primary btn-md mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg"
          >
            <Users size={16} />
            View Applicants
          </Link>
        </aside>
      </div>
    </div>
  );
}